import axiosInstance from '../../../lib/axios';
import type {
  CreateVideoJobInput,
  EstimateVideoInput,
  GenerateVideoConfig,
  UploadedImage,
  VideoJob,
  VideoJobPage,
  VideoOperation,
} from '../types';

const BASE = '/v1/services/generate-video';

export async function getGenerateVideoConfig(): Promise<GenerateVideoConfig> {
  const response = await axiosInstance.get(`${BASE}/config`);
  return response.data.data;
}

/** Exact server estimate for the current settings (dimension-based prices need the server). */
export async function estimateVideoJob(
  input: EstimateVideoInput,
  signal?: AbortSignal
): Promise<number> {
  const response = await axiosInstance.post(`${BASE}/estimate`, input, {
    signal,
    skipErrorToast: true,
  });
  return response.data.estimatedTokens ?? response.data.data?.estimatedTokens;
}

export async function uploadVideoInputImage(
  file: File,
  operation: VideoOperation
): Promise<UploadedImage> {
  const form = new FormData();
  form.append('file', file);
  form.append('operation', operation);
  const response = await axiosInstance.post(`${BASE}/uploads`, form, { skipErrorToast: true });
  return response.data.data;
}

/** Errors are shown inline by the form (402 needs a buy-tokens CTA), so no global toast. */
export async function createVideoJob(
  input: CreateVideoJobInput,
  idempotencyKey: string
): Promise<VideoJob> {
  const response = await axiosInstance.post(`${BASE}/jobs`, input, {
    headers: { 'Idempotency-Key': idempotencyKey },
    skipErrorToast: true,
  });
  return response.data.data;
}

export async function listVideoJobs(params: {
  cursor?: string;
  limit?: number;
}): Promise<VideoJobPage> {
  const response = await axiosInstance.get(`${BASE}/jobs`, { params });
  return response.data.data;
}

export async function getVideoJob(jobId: string): Promise<VideoJob> {
  const response = await axiosInstance.get(`${BASE}/jobs/${encodeURIComponent(jobId)}`, {
    skipErrorToast: true,
  });
  return response.data.data;
}

export async function cancelVideoJob(jobId: string): Promise<VideoJob> {
  const response = await axiosInstance.post(`${BASE}/jobs/${encodeURIComponent(jobId)}/cancel`);
  return response.data.data;
}

/** Authenticated fetch of a FileAsset (preview/download need the Bearer token). */
export async function getFileBlob(path: string): Promise<Blob> {
  const response = await axiosInstance.get(path, { responseType: 'blob' });
  return response.data;
}

export interface FileShareLink {
  url: string;
  expiresAt: string;
}

/** Time-limited public URL of a FileAsset, so social networks can fetch it. */
export async function getFileShareLink(publicId: string): Promise<FileShareLink> {
  const response = await axiosInstance.get(`/v1/files/${encodeURIComponent(publicId)}/share-link`, {
    skipErrorToast: true,
  });
  return response.data.data;
}
